import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

const SITE_KEY = "0x4AAAAAACw5cIkrDxgNyfLG";
const SCRIPT_ID = "cf-turnstile-script";

type Props = {
  onVerify: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
};

export type TurnstileWidgetHandle = {
  reset: () => void;
};

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: Record<string, unknown>
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

export const TurnstileWidget = forwardRef<TurnstileWidgetHandle, Props>(
  ({ onVerify, onExpire, onError }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    const mountedRef = useRef(true);

    // Store callbacks in refs to avoid re-renders
    const onVerifyRef = useRef(onVerify);
    const onExpireRef = useRef(onExpire);
    const onErrorRef = useRef(onError);
    onVerifyRef.current = onVerify;
    onExpireRef.current = onExpire;
    onErrorRef.current = onError;

    const resetWidget = () => {
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.reset(widgetIdRef.current);
        } catch {}
      }
    };

    useImperativeHandle(ref, () => ({ reset: resetWidget }), []);

    useEffect(() => {
      mountedRef.current = true;

      const doRender = () => {
        if (!mountedRef.current || !containerRef.current || !window.turnstile) return;
        if (widgetIdRef.current) return;

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          callback: (token: string) => onVerifyRef.current(token),
          "expired-callback": () => {
            onExpireRef.current?.();
            resetWidget();
          },
          "error-callback": () => {
            onErrorRef.current?.();
            resetWidget();
          },
          theme: "auto",
        });
      };

      if (window.turnstile) {
        doRender();
        return;
      }

      if (!document.getElementById(SCRIPT_ID)) {
        const script = document.createElement("script");
        script.id = SCRIPT_ID;
        script.src =
          "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
        script.async = true;
        document.head.appendChild(script);
      }

      const interval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(interval);
          doRender();
        }
      }, 100);

      return () => {
        mountedRef.current = false;
        clearInterval(interval);
        if (widgetIdRef.current && window.turnstile) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch {}
          widgetIdRef.current = null;
        }
      };
    }, []);

    return <div ref={containerRef} className="flex justify-center" />;
  }
);

TurnstileWidget.displayName = "TurnstileWidget";
